import React, { useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Stack,
    Button,
    alpha,
    IconButton,
    Pagination,
    Avatar,
    Menu,
    MenuItem,
    Drawer
} from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { DossierPreviewCanvas } from './DossierPreviewCanvas';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import apiClient from '../../../services/api-client';

// --- CONFIGURATION DES STATUTS ---
const STATUS_MAP = {
    'BROUILLON': { label: 'Brouillon', bg: '#9e9e9e', color: '#fff' },
    'CONFIRME': { label: 'Confirmé', bg: '#2e7d32', color: '#fff' },
    'ANNULE': { label: 'Annulé', bg: '#d32f2f', color: '#fff' },
};

export function DossiersTable({
    data = [],
    page = 0,
    totalPages = 0,
    onPageChange,
    currentStatus = 'ALL',
    onStatusChange
}) {

    const tabs = [
        { id: 'ALL', label: 'Toutes' },
        { id: 'BROUILLON', label: 'Brouillons' },
        { id: 'CONFIRME', label: 'Confirmés' },
        { id: 'ANNULE', label: 'Annulés' },
    ];

    // --- ÉTATS LOCAUX ---
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    const openMenu = Boolean(anchorEl);

    // --- GESTION DU MENU ---
    const handleMenuClick = (event, row) => {
        setAnchorEl(event.currentTarget);
        setSelectedRow(row);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        // 💡 On NE MET PAS selectedRow à null ici, sinon le Drawer perd ses données !
    };

    // --- ACTIONS DU MENU ---
    const handleConsulterClick = () => {
        handleMenuClose();
        setIsDrawerOpen(true);
    };

    const handleValiderClick = () => {
        handleMenuClose();
        console.log("Valider le dossier:", selectedRow?.id);
    };

    const handleAnnulerClick = () => {
        handleMenuClose();
        console.log("Annuler le dossier:", selectedRow?.id);
    };

    // --- FERMETURE DRAWER ---
    const handleDrawerClose = () => {
        setIsDrawerOpen(false);
    };

    // --- TELECHARGER DOSSIER ---
    const handleDownloadClick = async () => {
        if (!selectedRow?.id) return;

        try {
            const response = await apiClient.get(`/api/orders/${selectedRow.id}/documents`, {
                responseType: 'blob',
                headers: {
                    'Accept': 'application/zip'
                }
            });

            const blob = new Blob([response.data], { type: 'application/zip' });
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;

            const fileName = `Dossier_${selectedRow.referenceCode || selectedRow.id}.zip`;
            link.setAttribute('download', fileName);

            document.body.appendChild(link);
            link.click();

            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);

            console.log("Téléchargement démarré pour le dossier:", selectedRow.id);

        } catch (error) {
            console.error("Erreur lors du téléchargement :", error);
        }
    };

    // --- UTILITAIRES ---
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('fr-FR');
    };

    return (
        <Paper elevation={0} sx={{ p: 4, bgcolor: 'white', minHeight: 550, display: 'flex', flexDirection: 'column' }}>

            {/* --- HEADER & FILTRES --- */}
            <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center", mb: 4 }}>
                <Typography variant="h5" color="secondary.main">Dossiers</Typography>
                <Stack direction="row" spacing={1} sx={{ bgcolor: alpha('#000', 0.02), p: 0.5, borderRadius: '20px' }}>
                    {tabs.map((tab) => (
                        <Button
                            key={tab.id}
                            onClick={() => onStatusChange && onStatusChange(tab.id)}
                            size='small'
                            sx={{
                                height: '30px', borderRadius: '6px', fontSize: '0.75rem',
                                color: currentStatus === tab.id ? 'secondary.main' : 'text.secondary',
                                bgcolor: currentStatus === tab.id ? '#efebe2' : 'transparent',
                                '&:hover': { bgcolor: alpha('#c9a84c', 0.1) },
                                boxShadow: currentStatus === tab.id ? '0 2px 4px rgba(0,0,0,0.05)' : 'none',
                                textTransform: 'none', fontWeight: currentStatus === tab.id ? 700 : 500
                            }}
                        >
                            {tab.label}
                        </Button>
                    ))}
                </Stack>
            </Box>

            {/* --- TABLEAU --- */}
            <TableContainer sx={{ flexGrow: 1, overflowY: 'auto' }}>
                <Table stickyHeader sx={{ minWidth: 800 }}>
                    <TableHead>
                        <TableRow>
                            {['RÉFÉRENCE', 'CLIENT', 'CONTACT', 'PRESTATIONS', 'MONTANT', 'CREER LE', 'AGENT', 'STATUT', 'ACTIONS'].map((head) => (
                                <TableCell key={head} sx={{
                                    fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary',
                                    letterSpacing: 1, borderBottom: '1px solid rgba(0,0,0,0.05)'
                                }}>
                                    {head}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.length === 0 ? (
                            <TableRow sx={{ height: 200 }}>
                                <TableCell colSpan={9} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", margin: "auto" }}>
                                        <Avatar sx={{
                                            bgcolor: 'secondary.main', color: 'primary.main',
                                            width: 60, height: 60, fontSize: '2rem', fontWeight: 700,
                                            border: '3px solid', borderColor: alpha('#c9a84c', 0.3), mb: 2
                                        }}>
                                            <StorageIcon fontSize="large" />
                                        </Avatar>
                                        <Typography variant="body2">Aucun dossier trouvé.</Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ) : (
                            data.map((row) => {
                                const statusStyle = STATUS_MAP[row?.statut] || { label: row?.statut, bg: '#000', color: '#fff' };

                                return (
                                    <TableRow key={row.id} sx={{ '&:hover': { bgcolor: alpha('#c9a84c', 0.02) } }}>
                                        <TableCell sx={{ color: 'primary.main', fontWeight: 600, fontSize: '0.85rem' }}>
                                            {row.referenceCode || 'N/A'}
                                        </TableCell>
                                        {/* 💡 SÉCURISATION DES DONNÉES IMBRIQUÉES avec "?." */}
                                        <TableCell sx={{ fontWeight: 500 }}>{row.client?.nomComplet || 'Inconnu'}</TableCell>
                                        <TableCell color="text.secondary">{row.client?.telephone || '-'}</TableCell>
                                        <TableCell color="text.secondary">{row.prestations?.length || 0}</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>{row.prixTotal?.toLocaleString('fr-FR') || 0} FCFA</TableCell>
                                        <TableCell color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                            {formatDate(row.dateCreation)}
                                        </TableCell>
                                        <TableCell color="text.secondary">{row.agentUsername || 'Système'}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={statusStyle.label}
                                                size="small"
                                                sx={{
                                                    bgcolor: statusStyle.bg, color: statusStyle.color,
                                                    fontWeight: 700, fontSize: '0.65rem', borderRadius: '4px',
                                                    height: 24, minWidth: '70px'
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <IconButton
                                                size="small"
                                                onClick={(e) => handleMenuClick(e, row)}
                                                aria-controls={openMenu ? 'action-menu' : undefined}
                                                aria-haspopup="true"
                                                aria-expanded={openMenu ? 'true' : undefined}
                                            >
                                                <MoreVertIcon sx={{ fontSize: '1.2rem', color: "primary.main" }} />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* --- MENU CONTEXTUEL --- */}
            <Menu
                id="action-menu"
                anchorEl={anchorEl}
                open={openMenu}
                onClose={handleMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{
                    elevation: 3,
                    sx: { borderRadius: '8px', minWidth: '120px' }
                }}
            >
                {/* 💡 CORRECTION DU ONCLICK : On passe une référence de fonction, pas une exécution */}
                <MenuItem onClick={handleConsulterClick} sx={{ fontSize: '0.8rem' }}>Consulter</MenuItem>
                <MenuItem onClick={handleValiderClick} sx={{ fontSize: '0.8rem' }}>Valider</MenuItem>
                <MenuItem onClick={handleAnnulerClick} sx={{ fontSize: '0.8rem', color: 'error.main' }}>Annuler</MenuItem>
            </Menu>

            {/*--- DRAWER (PANNEAU LATÉRAL) --- */}
            <Drawer
                anchor="right"
                open={isDrawerOpen}
                onClose={handleDrawerClose}
                PaperProps={{
                    sx: {
                        width: { xs: '100%', md: '60%', lg: '50%' },
                        height: '100vh',
                        maxWidth: 800,
                        p: 3,
                        borderRadius: '16px 0 0 16px',
                    }
                }}
            >
                {/* 💡 TOUT LE CONTENU EST SÉCURISÉ DANS LE BLOC {selectedRow && ...} */}
                {selectedRow && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 3 }}>
                        <Box sx={{ overflowY: 'auto', flexGrow: 1, mt: '10vh' }}>
                            <DossierPreviewCanvas dossier={selectedRow} />
                        </Box>

                        <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                            <Button variant="outlined" onClick={handleDrawerClose}>
                                Fermer
                            </Button>
                            {selectedRow.statut === 'BROUILLON' && (
                                <Button variant="contained" color="primary" onClick={handleValiderClick}>
                                    Valider le dossier
                                </Button>
                            )}
                            <Button variant="contained" color='secondary' onClick={handleDownloadClick}
                                startIcon={<FolderZipIcon />}>
                                Télécharger
                            </Button>
                        </Box>
                    </Box>
                )}
            </Drawer>

            {/* --- PAGINATION --- */}
            {totalPages > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, pt: 2, borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                    <Pagination
                        count={totalPages}
                        page={page + 1}
                        onChange={(event, value) => onPageChange(value - 1)}
                        color="secondary"
                        shape="rounded"
                    />
                </Box>
            )}
        </Paper>
    );
}